import React, { useEffect, useMemo } from "react";
import { withTranslation } from "react-i18next";
import PropTypes from "prop-types";

import { CKEditor } from "@ckeditor/ckeditor5-react";
import Editor from "../../customBuilds/ckeditor5-custom-classic-mention-build";

const toPixelValue = (value) =>
  typeof value === "number" ? `${value}px` : value;

const getEffectiveMinHeight = (minHeight, maxHeight) => {
  if (!minHeight && !maxHeight) {
    return "0";
  }
  if (!maxHeight) {
    return toPixelValue(minHeight);
  }
  if (!minHeight) {
    return "0";
  }
  const minValue =
    typeof minHeight === "number" ? minHeight : parseInt(minHeight, 10);
  const maxValue =
    typeof maxHeight === "number" ? maxHeight : parseInt(maxHeight, 10);
  return minValue > maxValue ? toPixelValue(maxHeight) : toPixelValue(minHeight);
};

const CKEditorText = (props) => {
  const {
    t,
    setDescriptionData,
    descriptionData,
    disabled,
    minWidth,
    minHeight,
    maxHeight,
  } = props;

  const editorId = useMemo(
    () => `ck-editor-text-${Math.random().toString(36).slice(2, 11)}`,
    []
  );

  const effectiveMinHeight = getEffectiveMinHeight(minHeight, maxHeight);
  const effectiveMaxHeight = maxHeight ? toPixelValue(maxHeight) : null;

  useEffect(() => {
    const styleId = `${editorId}-styles`;
    let styleTag = document.getElementById(styleId);

    if (!styleTag) {
      styleTag = document.createElement("style");
      styleTag.id = styleId;
      document.head.appendChild(styleTag);
    }

    styleTag.textContent = `
      #${editorId} .ck-editor__editable_inline {
        min-height: ${effectiveMinHeight} !important;
        ${effectiveMaxHeight ? `max-height: ${effectiveMaxHeight} !important;` : ""}
        overflow-y: auto !important;
      }
    `;

    return () => {
      if (styleTag && styleTag.parentNode) {
        styleTag.parentNode.removeChild(styleTag);
      }
    };
  }, [editorId, effectiveMinHeight, effectiveMaxHeight]);

  return (
    <div id={editorId} style={{ minWidth, minHeight: effectiveMinHeight }}>
      <CKEditor
        editor={Editor}
        disabled={disabled}
        data={descriptionData}
        config={{
          extraPlugins: ["FontFamily"],
          fontSize: {
            options: [
              "tiny",
              "small",
              "default",
              "big",
              "huge",
              8,
              9,
              10,
              11,
              12,
              14,
              16,
              18,
              20,
              22,
              24,
              26,
              28,
              36,
              48,
            ],
          },
          toolbar: [
            "heading",
            "|",
            "fontFamily",
            "fontSize",
            "FontColor",
            "FontBackgroundColor",
            "|",
            "bold",
            "italic",
            "link",
            // "bulletedList",
            // "numberedList",
            // "blockQuote",
            // "ckfinder",
            "|",
            // "imageTextAlternative",
            // "imageUpload",
            // "imageStyle:full",
            // "imageStyle:side",
            // "|",
            "mediaEmbed",
            "insertTable",
            // "tableColumn",
            // "tableRow",
            // "mergeTableCells",
            "|",
            "undo",
            "redo",
          ],
        }}
        onChange={(event, editor) => {
          const data = editor.getData();
          setDescriptionData(data);
        }}
      />
    </div>
  );
};

CKEditorText.propTypes = {
  t: PropTypes.func.isRequired,
  setDescriptionData: PropTypes.func.isRequired,
  descriptionData: PropTypes.string,
  disabled: PropTypes.bool,
  minWidth: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  minHeight: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  maxHeight: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
};

CKEditorText.defaultProps = {
  descriptionData: null,
  disabled: false,
  minWidth: 200,
  minHeight: 200,
  maxHeight: null,
};

export default withTranslation()(CKEditorText);

